import React, { useEffect, useState } from "react";
import { DeviceSnapshot } from "../../../api/types/CamerasAdminVM";
import FTModal from "../../../components/common/FTModal";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { useTranslation } from "react-i18next";
import SplitView from "../../../assets/svg/split_view.svg";
import FullScreen from "../../../assets/svg/fullscreen.svg";
import svgIcons from "../../../assets/svg/svgIcons";
import { CAMERA_ID_TYPES } from "../constants";
import { AdminFormFieldCheckboxGroup } from "../../../components/admin/formFields";
import { toast } from "react-toastify";

interface ParkingPhotoProps {
  photos: DeviceSnapshot[] | [];
  displayAsModal?: boolean;
}

const ParkingPhotos = ({
  photos,
  displayAsModal = true,
}: ParkingPhotoProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });
  const [viewOptionVisibile, setViewOptionVisible] = useState(false);
  // Step 1: Set up state for selected values
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [cameraViews, setCameraViews] = useState({
    [CAMERA_ID_TYPES.in_cabin]: {
      label: t("cabin_camera"),
      value: CAMERA_ID_TYPES.in_cabin,
      selected: true,
      photos:
        photos?.filter((photo) =>
          photo.image.includes(CAMERA_ID_TYPES.in_cabin)
        ) || [],
    },
    [CAMERA_ID_TYPES.front]: {
      label: t("road_facing_camera"),
      value: CAMERA_ID_TYPES.front,
      selected: true,
      photos:
        photos?.filter((photo) =>
          photo.image.includes(CAMERA_ID_TYPES.front)
        ) || [],
    },
    [CAMERA_ID_TYPES.extended]: {
      label: t("extended_camera"),
      value: CAMERA_ID_TYPES.extended,
      selected: true,
      photos:
        photos?.filter((photo) =>
          photo.image.includes(CAMERA_ID_TYPES.extended)
        ) || [],
    },
    [CAMERA_ID_TYPES.rear]: {
      label: t("rear_facing_camera"),
      value: CAMERA_ID_TYPES.rear,
      selected: true,
      photos:
        photos?.filter((photo) => photo.image.includes(CAMERA_ID_TYPES.rear)) ||
        [],
    },
  });
  useEffect(() => {
    const updatedCameraViews = { ...cameraViews };
    Object.keys(updatedCameraViews).forEach((key) => {
      const cameraId = key as CAMERA_ID_TYPES;
      updatedCameraViews[cameraId].photos = photos.filter((photo) =>
        photo.image.includes(cameraId)
      );
    });
    setCameraViews(updatedCameraViews);
    // Set selectedValues to include all values from cameraViews
    const allValues = Object.values(updatedCameraViews)
      .filter((view) => view.photos && view.photos.length > 0)
      .map((view) => view.value);
    setSelectedValues(allValues);
  }, [photos]);

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showParkingPhotoView: false }));
  };

  const activeCameraViews = Object.values(cameraViews).filter(
    (view) =>
      view.photos &&
      view.photos.length > 0 &&
      selectedValues.includes(view.value)
  );

  const getLayoutClasses = (count: number, index: number) => {
    if (count === 1) return "w-full h-full";
    if (count === 2) return "w-1/2 h-full";
    if (count === 3) return index === 0 ? "w-full h-1/2" : "w-1/2 h-1/2";
    if (count === 4) return "w-1/2 h-1/2";
    return "w-full h-full"; // fallback
  };

  if (displayAsModal && !modalsState.showParkingPhotoView) {
    return null;
  }

  const ViewSelection = () => {
    if (!viewOptionVisibile) return null;

    const options = Object.values(cameraViews) // Convert object to array
      .filter((view) => view.photos && view.photos.length > 0) // Include only views with photos
      .map((view) => ({
        label: view.label, // Use the label of the view
        value: view.value, // Use the value of the view
      }));

    // Step 2: Handle changes to selected values
    const handleCheckboxChange = (values: string[]) => {
      // Ensure at least one checkbox is selected
      if (values.length < 1) {
        toast.warning(t("atleat_one_view_warning"));
        return;
      }
      setSelectedValues(values);
    };
    return (
      <div className="absolute bottom-20 right-12 ounded-md bg-white shadow-lg z-50">
        <AdminFormFieldCheckboxGroup
          layout="column"
          options={options}
          values={selectedValues}
          onChange={handleCheckboxChange}
          selectionMode="multiple"
        />
      </div>
    );
  };

  return (
    <>
      {displayAsModal && (
        <div
          className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
          onClick={hideModal}
        />
      )}
      <div
        className={
          displayAsModal
            ? "fixed inset-y-1/2 inset-x-1/2 w-full h-full pt-10 bg-black/80 overflow-hidden " +
              "Xbg-white rounded-xl shadow-lg z-modal grid grid-cols-9"
            : "relative w-full h-full overflow-hidden rounded-xl grid grid-cols-9"
        }
        style={displayAsModal ? { transform: "translate(-50%, -50%)" } : {}}
      >
        {/* Close button */}
        {displayAsModal && (
          <div className="absolute top-12 right-4 flex flex-row gap-2 z-50 p-2 bg-white/20 rounded-full col-span-9">
            <button
              className="group size-10 flex items-center justify-center rounded-full bg-black hover:bg-gray-100 focus:ring focus:ring-white outline-0"
              onClick={hideModal}
            >
              <img
                src={svgIcons.CloseIcon}
                alt="Close"
                className="grayscale-0 brightness-0 invert group-hover:invert-0 opacity-50"
              />
            </button>
          </div>
        )}

        {/* Main content */}
        {/* <div className="col-span-9 h-[calc(100vh-10rem)] overflow-auto grid grid-cols-8 mt-20 justify-center justify-items-center">
          {Object.keys(cameraViews).map((photoObjKey) => {
            const typedKey = photoObjKey as keyof typeof cameraViews;
            const phtoObj = cameraViews[typedKey];
            return (
              <div className="border-white border-2 self-center col-span-4 h-full w-full bg-black">
                {phtoObj?.photos && phtoObj?.photos?.length > 0 ? (
                  <>
                    <span className="bg-black-100 text-white font-md text-md w-full px-4 py-2">
                      {phtoObj.label}
                    </span>
                    <img src={phtoObj.photos[0].image} alt={""} />
                  </>
                ) : (
                  <>
                    <span className="bg-black-100 text-white font-md text-md px-4 py-2">
                      {phtoObj.label}
                    </span>
                    <div className="flex h-full w-full justify-center items-center">
                      <h2 className="text-white font-md text-md w-64">
                        No Photo available
                      </h2>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div> */}

        <div
          className={`col-span-9 h-[calc(100vh-10rem)] overflow-auto${displayAsModal ? " mt-20" : ""} flex flex-wrap`}
        >
          {activeCameraViews?.length > 0 ? (
            activeCameraViews.map((view, index) => (
              <div
                key={view.value}
                className={`${getLayoutClasses(activeCameraViews.length, index)} flex justify-center items-center`}
              >
                <div
                  className={`w-full h-full${displayAsModal ? " border-white border-2" : ""} bg-black relative`}
                >
                  <span className="absolute top-0 left-0 bg-black-100 text-white font-md text-md px-4 py-2">
                    {view.label}
                  </span>
                  {view.photos?.length > 0 ? (
                    <img
                      src={view.photos[0].image}
                      alt={view.label}
                      className="object-contain w-full h-full"
                    />
                  ) : (
                    <div className="flex justify-center items-center h-full text-white">
                      {t("no_photo_available")}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="w-full h-full flex justify-center items-center text-white">
              {t("no_photo_available")}
            </div>
          )}
        </div>

        {/* Control strip */}
        <div className="absolute bottom-0 left-0 right-0 flex flex-row col-span-2 place-content-between bg-black border-t border-gray-300 p-4">
          <h2 className="text-white">
           {selectedValues?.length > 1 ? t("split_view") : t("single_view")}
          </h2>

          <button
            type="button"
            onClick={() => setViewOptionVisible(!viewOptionVisibile)}
          >
            <img src={SplitView} alt="Split View" />
          </button>
        </div>
        <ViewSelection />
      </div>
    </>
  );
};

export default ParkingPhotos;
